import { useContext, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import * as giveawayApis from 'apis/giveaways';
import { DeviceContext } from '../../../DeviceContext';
import ShowMore from '../ShowMore';
import './GiveawayListStatic.scss';

/*
  Giveaway Listing layout, converted from the giveaway-listing.html mockup.
  Pluggable, self-contained component:
  - All CSS is scoped under ".gl-page" with "gl-" prefixed classes,
    so it can be dropped into the public site or the Dashboard
    without colliding with existing global styles.
  - Renders real giveaway data from the API. Empty fields/sections
    are omitted (no static content fallbacks).
  - On the public site it fetches all active giveaways; inside the
    dashboard it fetches the logged-in user's giveaways (which come
    back with a "tickets" field) and additionally renders the
    entries/winner/live-draw info that the legacy dashboard card had.
*/

const formatDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })} IST`;
};

const GiveawayListStatic = () => {
  const location = useLocation();
  const { isMobile } = useContext(DeviceContext);
  const assetUrl = process.env.REACT_APP_TAURIAN_ASSET_URL;
  const isDashboard = location.pathname.startsWith('/dashboard');

  const [giveaways, setGiveaways] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = isDashboard
          ? await giveawayApis.fetchUserGiveaways()
          : await giveawayApis.fetchGiveaways();
        setGiveaways(response.data || []);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, [isDashboard]);

  const detailsHref = (id) => (isDashboard ? `/dashboard/giveaway/${id}` : `/Giveaway/${id}`);

  return (
    <div className="gl-page">
      <div className="gl-container">

        <div className="gl-list">
          {giveaways.length === 0 && <div className="gl-empty">No giveaways to show right now.</div>}

          {giveaways.map((giveaway) => {
            const title = [giveaway.brandName, giveaway.firstName, giveaway.lastName].filter(Boolean).join(' ');
            const description = giveaway.shortDescription || giveaway.description;
            const imagePath = giveaway.images?.[0] || giveaway.image;
            const image = imagePath ? `${assetUrl}${imagePath}` : '';
            const specs = giveaway.attributes?.length
              ? giveaway.attributes.slice(0, 3).map((attr) => ({ icon: attr.logo, value: attr.value, label: attr.text }))
              : [];
            const drawDate = giveaway.drawDate ? new Date(giveaway.drawDate) : null;
            const lastEntryDate = giveaway.endDate ? new Date(giveaway.endDate) : null;
            const tickets = giveaway.tickets || [];

            return (
              <div className="gl-card" key={giveaway.id}>

                <div className="gl-row">

                  <div className="gl-card-img">
                    <span className={`gl-tag ${giveaway.isActive ? '' : 'gl-upcoming'}`}>{giveaway.isActive ? 'LIVE' : 'UPCOMING'}</span>
                    {image && <img src={image} alt={title} />}
                  </div>

                  <div className="gl-card-body">
                    <span className={`gl-category ${giveaway.isFree ? 'gl-free' : 'gl-paid'}`}>{giveaway.isFree ? 'FREE ENTRY' : 'SUBSCRIBERS ONLY'}</span>
                    {title && <h3>{title}</h3>}
                    {description && <p>{description}</p>}

                    {specs.length > 0 && (
                      <div className="gl-stats">
                        {specs.map((spec, index) => (
                          <div className="gl-stat" key={`${spec.label}-${index}`}>
                            {spec.icon && <i className={spec.icon}></i>}
                            <strong>{spec.value}</strong>
                            <span>{spec.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="gl-card-side">
                    {drawDate && (
                      <div className="gl-date-block">
                        <div className="gl-date-label gl-draw"><i className="fa-solid fa-calendar-days"></i> DRAW DATE</div>
                        <div className="gl-date-value">{formatDate(drawDate)}</div>
                        <div className="gl-date-time">{formatTime(drawDate)}</div>
                      </div>
                    )}
                    {lastEntryDate && (
                      <div className="gl-date-block">
                        <div className="gl-date-label gl-entry"><i className="fa-solid fa-clock"></i> LAST ENTRY</div>
                        <div className="gl-date-value">{formatDate(lastEntryDate)}</div>
                        <div className="gl-date-time">{formatTime(lastEntryDate)}</div>
                      </div>
                    )}
                    <Link to={detailsHref(giveaway.id)} className="gl-view-btn">VIEW DETAILS <i className="fa-solid fa-arrow-right"></i></Link>
                  </div>

                </div>

                {isDashboard && (tickets.length > 0 || giveaway.drawLink || giveaway.winner) && (
                  <div className="gl-dash-extra">

                    {tickets.length > 0 && (
                      <div className="gl-tickets">
                        <span className="gl-tickets-label"><i className="fa-solid fa-ticket"></i> YOUR ENTRIES</span>
                        <ShowMore className="gl-ticket-chips">
                          {tickets.map((ticket, index) => {
                            const hidden = (isMobile && index > 1) || (!isMobile && index > 5);
                            return (
                              <span className={`gl-ticket-chip ${hidden ? 'content' : ''}`} key={`${ticket}-${index}`}>{ticket}</span>
                            );
                          })}
                        </ShowMore>
                      </div>
                    )}

                    {(giveaway.drawLink || giveaway.winner) && (
                      <div className="gl-dash-meta">
                        {giveaway.drawLink && (
                          <a href={giveaway.drawLink} target="_blank" rel="noopener noreferrer" className="gl-draw-link">
                            <i className="fa-solid fa-video"></i>Live draw details
                          </a>
                        )}
                        {giveaway.winner && (
                          <span className="gl-winner"><i className="fa-solid fa-trophy"></i>Winner: {giveaway.winner?.firstName}</span>
                        )}
                      </div>
                    )}

                  </div>
                )}

              </div>
            );
          })}
        </div>

        {!isDashboard && giveaways.length > 0 && (
          <div className="gl-trust-strip">

            <div className="gl-trust-item">
              <i className="fa-solid fa-shield-halved"></i>
              <h4>100% SECURE</h4>
              <p>Fair &amp; transparent draws with verified winners</p>
            </div>

            <div className="gl-trust-item">
              <i className="fa-solid fa-trophy"></i>
              <h4>AMAZING PRIZES</h4>
              <p>Premium motorcycles, cars and more up for grabs</p>
            </div>

            <div className="gl-trust-item">
              <i className="fa-solid fa-ticket"></i>
              <h4>EASY ENTRY</h4>
              <p>Simple entry process for maximum fun</p>
            </div>

            <div className="gl-trust-item">
              <i className="fa-solid fa-users"></i>
              <h4>JOIN THE COMMUNITY</h4>
              <p>Be part of thousands of enthusiasts like you</p>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default GiveawayListStatic;
